import i18n, { InitOptions } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import { SUPPORT_LANGUAGES } from '../constants/supportLanguages';
import { I18nPackageConfig, I18nPackageResources } from '../types';

// 등록된 모든 패키지 리소스
const registeredPackages: I18nPackageConfig[] = [];

export function registerPackageResources(config: I18nPackageConfig) {
  registeredPackages.push(config);

  /*
    initializeI18n() 은 호출 시점까지 등록된 리소스만 합친다. 앱이 i18n setup 을 패키지보다
    먼저 import 하면(패키지는 화면 코드가 불러올 때 평가된다) 그 패키지 문구가 통째로 빠져
    영어 화면에 한국어 키가 그대로 나왔다. 이미 초기화됐으면(store 가 있으면) 바로 싣는다.
  */
  if (i18n.store) {
    Object.entries(config.resources).forEach(([lng, namespaces]) => {
      Object.entries(namespaces).forEach(([ns, resources]) => {
        i18n.addResourceBundle(lng, ns, resources, true, true);
      });
    });
  }
}

export function initializeI18n(options?: {
  debug?: boolean;
  fallbackLng?: InitOptions['fallbackLng'];
  defaultNS?: string;
  additionalResources?: I18nPackageConfig[];
  keySeparator?: InitOptions['keySeparator'];
  nsSeparator?: InitOptions['nsSeparator'];
  pluralSeparator?: string;
  detection?: InitOptions['detection'];
  react?: InitOptions['react'];
}) {
  const {
    additionalResources,
    keySeparator,
    nsSeparator,
    pluralSeparator,
    detection,
    react,
    ...restOptions
  } = options ?? {};

  // 기본 옵션과 사용자 지정 옵션 병합
  const defaultOptions = {
    debug: false,
    fallbackLng: 'ko' as InitOptions['fallbackLng'],
    defaultNS: 'common',
  };

  const mergedOptions = { ...defaultOptions, ...restOptions };

  // 모든 리소스 병합
  let allResources: I18nPackageResources = {};

  // 등록된 패키지 리소스 추가
  [...registeredPackages, ...(additionalResources || [])].forEach(
    (packages) => {
      Object.entries(packages.resources).forEach(([lang, namespaces]) => {
        if (!allResources[lang]) {
          allResources[lang] = {};
        }

        Object.entries(namespaces).forEach(([ns, resources]) => {
          allResources[lang][ns] = resources;
        });
      });
    },
  );

  return i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      ...mergedOptions,
      debug: import.meta.env.NODE_ENV === 'development',
      resources: allResources,
      supportedLngs: Object.keys(SUPPORT_LANGUAGES),
      interpolation: {
        escapeValue: false,
      },
      ...(keySeparator !== undefined && { keySeparator }),
      ...(nsSeparator !== undefined && { nsSeparator }),
      ...(pluralSeparator !== undefined && { pluralSeparator }),
      ...(detection !== undefined && { detection }),
      ...(react !== undefined && { react }),
    });
}

export { i18n };
